import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, contentChild, effect, forwardRef, input, output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { TranslocoModule } from '@ngneat/transloco';
import { ZardSelectItemComponent } from '../../../app/shared/components/select/select-item.component';
import { ZardSelectComponent } from '../../../app/shared/components/select/select.component';
import { ZardSelectSizeVariants } from '../../../app/shared/components/select/select.variants';
import { provideSolvaceIcons } from '../../svz-icons/solvace-icons';
import { SvzValidationMessageComponent } from '../../svz-validation-message/svz-validation-message.component';
import { SVZ_LEGACY_ICON_PROVIDERS, resolveLegacyIconName } from '../../zard-adapters/legacy-zard-icon';
import { SvzControl } from '../svz-control';
import { SVZ_GROUP_HEADER_CLASSES, SVZ_OPTION_ROW_CLASSES, SVZ_SEARCH_BOX_CLASSES } from '../svz-option-row.classes';
import { ISvzSelectOption } from './interfaces/svz-select.interface';

interface InternalSelectOption extends ISvzSelectOption {
  key: string;
}

interface SelectOptionGroup {
  trackKey: string;
  groupKey: string | null;
  groupLabel: string | null;
  options: InternalSelectOption[];
}

@Component({
  selector: 'svz-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-select.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SvzSelectComponent), multi: true }, provideSolvaceIcons()],
  viewProviders: [...SVZ_LEGACY_ICON_PROVIDERS],
  standalone: true,
  imports: [NgIcon, ZardSelectComponent, ZardSelectItemComponent, NgTemplateOutlet, SvzValidationMessageComponent, TranslocoModule],
})
export class SvzSelectComponent extends SvzControl {
  protected readonly optionRowClasses = `w-full ${SVZ_OPTION_ROW_CLASSES}`;
  protected readonly groupHeaderClasses = SVZ_GROUP_HEADER_CLASSES;
  protected readonly searchBoxClasses = SVZ_SEARCH_BOX_CLASSES;

  readonly label = input<
    | string
    | {
        text: string;
        icon: string;
      }
    | null
  >(null);

  readonly placeholder = input('');

  /** Additive, defaults to the Zard `default` trigger (36px). `ds` is the prototype `.sd-trg`: 44px, 14px side padding,
   *  `#9aa0ac` placeholder and a 16px `#8a90a6` chevron. */
  readonly size = input<ZardSelectSizeVariants>('default');

  /**
   * Additive. `design-system` draws the prototypes' filter field: `.lbl-field` 13px/400 `#3a3a3a` 6px above the control
   * (`#filter-drawer .lbl-field`, Alerts/v1.1 11425) and the `.sd-trg` trigger (`size="ds"` unless the consumer sets a size).
   */
  readonly fieldAppearance = input<'default' | 'design-system'>('default');

  protected readonly isDesignSystemField = computed(() => this.fieldAppearance() === 'design-system');

  protected readonly effectiveSize = computed<ZardSelectSizeVariants>(() => (this.isDesignSystemField() && this.size() === 'default' ? 'ds' : this.size()));

  protected readonly fieldClasses = computed(() => (this.isDesignSystemField() ? 'flex w-full flex-col gap-1.5' : 'flex w-full flex-col gap-2'));

  protected readonly labelClasses = computed(() =>
    this.isDesignSystemField() ? 'inline-flex items-center gap-1 text-[13px] font-normal text-[#3a3a3a]' : 'inline-flex items-center gap-1 text-sm font-medium text-default'
  );

  protected readonly selectClasses = computed(() =>
    this.effectiveSize() === 'ds'
      ? 'w-full [&[data-state=open]_button]:border-primary [&_button_.text-muted-foreground]:text-[#9aa0ac] [&_button_ng-icon]:opacity-100 [&_button_svg:not([class*=text-])]:text-[#8a90a6]'
      : 'w-full [&[data-state=open]_button]:border-primary'
  );

  readonly options = input<ISvzSelectOption[] | null | undefined>(undefined);

  private readonly syncOptions = effect(() => {
    this._options = (this.options() ?? [])
      .filter((option) => !option?.isGroupLabel)
      .map((option, index) => ({
        ...option,
        key: `option-${index}`,
      }));

    this.filterOptions(this.searchText);
    this.cdr.markForCheck();
  });

  private _options: InternalSelectOption[] = [];

  readonly appearance = input<'default' | 'primary'>('default');

  readonly icon = input('');

  readonly readonly = input<boolean>(false);

  readonly readonlyStyle = input<'default' | 'chip' | 'transparent-chip'>('default');

  readonly truncateTextValue = input<boolean>(false);

  readonly inputId = input('');

  readonly fallbackTextValue = input('');

  readonly loading = input<boolean>(false);

  readonly externalSearch = input(false);

  readonly minCharactersExternalSearchLength = input(3);

  readonly hideOptionsWhenSelected = input(true);

  /** Forces the local-filter search box even at or below the 10-option auto-show threshold. */
  readonly alwaysShowSearch = input(false);

  readonly ngModelChange = output<unknown>();

  readonly searchTextChange = output<string>();

  readonly templateRef = contentChild(TemplateRef);

  protected searchText = '';
  protected filteredOptions: InternalSelectOption[] = [];
  private readonly collapsedGroups = new Set<string>();

  protected get showSearchInput(): boolean {
    return this.externalSearch() || this.alwaysShowSearch() || this._options.length > 10;
  }

  protected get labelText(): string {
    const label = this.label();
    return typeof label === 'string' ? label : (label?.text ?? '');
  }

  protected get resolvedLabelIcon(): string | null {
    const label = this.label();
    const icon = typeof label === 'string' ? '' : label?.icon;
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get resolvedPrefixIcon(): string | null {
    const icon = this.icon();
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get selectedOption(): InternalSelectOption | null {
    return this._options.find((option) => this.areValuesEqual(option.value, this.control.value)) ?? null;
  }

  protected get selectedOptionKey(): string {
    return this.selectedOption?.key ?? '';
  }

  protected get groupedOptions(): SelectOptionGroup[] {
    const filteredOptions = this.filteredOptions;
    if (!filteredOptions.length) {
      return [];
    }

    const groups: SelectOptionGroup[] = [];
    for (const option of filteredOptions) {
      const groupKey = option.groupKey ?? null;
      const groupLabel = option.groupLabel ?? null;
      const existingGroup = groups.find((group) => group.groupKey === groupKey && group.groupLabel === groupLabel);

      if (existingGroup) {
        existingGroup.options.push(option);
        continue;
      }

      groups.push({
        trackKey: groupKey ?? `ungrouped-${groups.length}`,
        groupKey,
        groupLabel,
        options: [option],
      });
    }

    return groups;
  }

  protected get hasLocalResults(): boolean {
    return this.groupedOptions.some((group) => group.options.length > 0);
  }

  filterOptionsByApi(): void {
    const trimmedSearch = this.searchText.trim();
    if (this.externalSearch() && trimmedSearch.length >= this.minCharactersExternalSearchLength()) {
      this.searchTextChange.emit(trimmedSearch);
    }
  }

  filterOptions(value: string | null): void {
    this.searchText = value ?? '';

    if (this.externalSearch()) {
      this.filteredOptions = [...this._options];
      return;
    }

    const normalizedFilter = this.searchText.trim().toLowerCase();
    if (!normalizedFilter) {
      this.filteredOptions = [...this._options];
      return;
    }

    this.filteredOptions = this._options.filter((option) => option.text.toLowerCase().includes(normalizedFilter));
  }

  openOptions(): void {}

  closeOptions(options: { fromOnDestroy?: boolean } = {}): void {
    if (!options.fromOnDestroy) {
      this.onTouched();
    }
  }

  remove(): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    this.control.setValue(null, { emitEvent: false });
    this.value = null;
    this.onChange(null);
    this.ngModelChange.emit(null);
    this.onTouched();
  }

  select(option: ISvzSelectOption): void {
    if (this.disabled() || this.readonly() || option.disabled) {
      return;
    }

    this.control.setValue(option.value, { emitEvent: false });
    this.value = option.value;
    this.onChange(option.value);
    this.ngModelChange.emit(option.value);
    this.onTouched();
  }

  toggleGroup(groupKey: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!groupKey) {
      return;
    }

    if (this.collapsedGroups.has(groupKey)) {
      this.collapsedGroups.delete(groupKey);
      return;
    }

    this.collapsedGroups.add(groupKey);
  }

  onOptionClick(option: ISvzSelectOption): void {
    this.select(option);
  }

  onOptionKeySelect(event: Event, option: ISvzSelectOption): void {
    event.preventDefault();
    this.select(option);
  }

  forceFocusItemWhenDownPressed(_event: unknown): void {}

  focusSibling(_event: unknown, _direction: 'next' | 'previous'): void {}

  isGroupCollapsed(groupKey: string | null): boolean {
    return groupKey ? this.collapsedGroups.has(groupKey) : false;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);
    this.filterOptions(this.searchText);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filterOptions(value);
    this.filterOptionsByApi();
  }

  onZardSelectionChange(selection: string | string[]): void {
    const selectedKey = Array.isArray(selection) ? selection[0] : selection;
    const option = this._options.find((item) => item.key === selectedKey);
    if (option) {
      this.select(option);
    }
  }

  protected renderOptionText(option: ISvzSelectOption | null | undefined): string {
    return option?.text ?? '';
  }

  private areValuesEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) {
      return true;
    }

    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }
}
